import React, { useEffect, useMemo, useRef, useState } from "react"
import TableContainer from "components/Common/TableContainer"
import moment from "moment"
import eye from "../../assets/images/icons/eye.svg"
import deleteIcon from "../../assets/images/icons/delete-red.svg"
import { Link } from "react-router-dom"
import DeleteConfirmationModal from "modals/deleteConfirmation"
import { UncontrolledTooltip } from "reactstrap"
import { usePermission } from "hooks/usePermission"

import { useFaqDeleteMutation, useLazyFaqListQuery } from "store/api/faqs/faq"
import { getTableSortingHeaderIcon } from "helpers/common"
import { PERMISSIONS } from "helpers/roles"

const defaultFilters = {
  search: "",
  page: 1,
  pageSize: 20,
  timestamp: "",
}

const FaqTable = ({ user_type }) => {
  const [getResults, { isFetching }] = useLazyFaqListQuery()
  const [filters, setFilters] = useState(defaultFilters)
  const [data, setData] = useState([])
  const [count, setCount] = useState(0)
  const [onDelete, { isLoading: isDeleting }] = useFaqDeleteMutation()
  const [isToast, setIsToast] = useState(false)
  const deleteRef = useRef()
  const { hasAnyPermission, hasPermission } = usePermission()

  const getStatus = (status) => {
    let className = ""
    switch (status) {
      case "ACTIVE":
        className = "open"
        break

      case "DISABLE":
        className = "closed"
        break

      default:
        className = "open"
        break
    }

    return (
      <div className={`card-status ${className}`}>
        <span></span>
        {status.charAt(0).toUpperCase() + status.slice(1).toLowerCase()}
      </div>
    )
  }

  //meta title
  document.title = "Faq | Prachay"

  useEffect(() => {
    getList()
  }, [filters, user_type])

  const getList = async () => {
    try {
      const { data } = await getResults({ ...filters, user_type }).unwrap()
      setData(data?.rows)
      setCount(data?.count)
    } catch (e) {}
  }

  const onActionSuccess = () => {
    setIsToast(false)
    deleteRef.current = null
    setFilters((prev) => ({ ...prev, page: 1, timestamp: moment().unix() }))
  }

  const columns = useMemo(() => {
    const baseColumns = [
      {
        header: ({ column }) => getTableSortingHeaderIcon(column, "Question"),
        accessorKey: "question",
        enableColumnFilter: false,
        enableSorting: true,
        cell: ({ row }) => <>{row?.original.question}</>,
      },
      {
        header: ({ column }) => getTableSortingHeaderIcon(column, "Status"),
        accessorKey: "status",
        enableColumnFilter: false,
        enableSorting: true,
        cell: ({ row }) => (
          <div className="d-flex align-items-center gap-2">
            <div>
              <div className="table-user-name">
                {getStatus(row?.original?.status)}
              </div>
            </div>
          </div>
        ),
      },
      {
        id: "actions",
        header: "Actions",
        cell: ({ row }) => {
          const viewTooltip = `editTooltip-${row?.original?.id}`
          const deleteTooltip = `deleteTooltip-${row?.original?.id}`

          return (
            <div className="d-flex justify-content-center align-items-center gap-3">
              <>
                {hasPermission(PERMISSIONS.FAQ_VIEW) && (
                  <div className="action-view">
                    <Link
                      to={`${
                        user_type === "INVESTOR" ? "/resources/faqs" : "/wp-faq"
                      }/view/${row?.original.id}`}
                      id={viewTooltip}
                    >
                      <img src={eye} alt="" />
                      <UncontrolledTooltip placement="top" target={viewTooltip}>
                        View
                      </UncontrolledTooltip>
                    </Link>
                  </div>
                )}

                {hasPermission(PERMISSIONS.FAQ_DELETE) && (
                  <div
                    className="action-delete"
                    onClick={() => {
                      deleteRef.current = row?.original
                      setIsToast(true)
                    }}
                    id={deleteTooltip}
                  >
                    <img src={deleteIcon} alt="" />
                    <UncontrolledTooltip placement="top" target={deleteTooltip}>
                      Delete
                    </UncontrolledTooltip>
                  </div>
                )}
              </>
            </div>
          )
        },
      },
    ]
    const canAccess = hasAnyPermission([
      PERMISSIONS.FAQ_VIEW,
      PERMISSIONS.FAQ_DELETE,
    ])

    if (canAccess) return baseColumns
    return baseColumns.filter((item) => item.id !== "actions")
  }, [hasAnyPermission])

  const FilterComponents = () => (
    <>
      <Link className="btn btn-secondary" to={"/resources/faqs/create"}>
        <i className="mdi mdi-plus me-1"></i> Create FAQs
      </Link>
    </>
  )

  return (
    <>
      <TableContainer
        isLoading={isFetching}
        count={count}
        filters={filters}
        setFilters={setFilters}
        columns={columns}
        data={data}
        isGlobalFilter={true}
        isCustomPageSize={true}
        FilterComponents={FilterComponents}
        isPagination={true}
        pagination="pagination"
        paginationWrapper="dataTables_paginate paging_simple_numbers"
        tableClass="table-bordered table-nowrap dt-responsive nowrap w-100 dataTable no-footer dtr-inline"
      />

      {isToast && deleteRef && (
        <DeleteConfirmationModal
          open={isToast}
          id={deleteRef?.current?.id}
          setOpen={setIsToast}
          isLoading={isDeleting}
          onClose={() => {
            setIsToast(false)
            deleteRef.current = null
          }}
          title={"Delete Faq"}
          description="Are you sure you want to delete this Faq?"
          onDelete={onDelete}
          onSuccess={onActionSuccess}
        />
      )}
    </>
  )
}

export default FaqTable
